import { addDays, format, parse } from 'date-fns';
import { debounce } from 'lodash-es';
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAbortEffect } from '@shared/hooks/useAbortEffect';
import { useDefinedContext } from '@shared/hooks/useDefinedContext';
import { useError } from '@shared/hooks/useError';
import { useServerSentEvents } from '@shared/hooks/useServerSentEvents';
import { END_OF_RESTAURANT_DAY } from '@shared/types/shifts';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import {
  getHostFloorPlansForRestaurantId,
  getTableTimers,
  TableTimer,
  type HostFloorPlanData,
} from 'restaurantAdmin/floorPlans/apiHelpers';
import { type OccupantType } from 'restaurantAdmin/reservations/apiHelpers';
import { toISODateFormat } from '@shared/utils/dateFormatters';
import { getOccupants, type ServiceOccupant } from '../apiHelpers';
import {
  DATE_PARAM,
  DATE_PARAM_FORMAT,
  OCCUPANT_ID_PARAM,
  OCCUPANT_TYPE_PARAM,
} from '../../../utils/constants';
import { useFetchOccupantDetails } from '../hooks/useFetchOccupantDetails';
import { useServiceSearchParams } from '../hooks/useServiceSearchParams';

export enum SidePanelSheetMode {
  None = 'none',
  ServerForm = 'serverForm',
  SplitMerge = 'splitMerge',
}

export type SidePanelSheetState =
  | { mode: SidePanelSheetMode.None }
  | { mode: SidePanelSheetMode.SplitMerge };

export type TableTimerDictionary = Record<string, TableTimer>;

export type TimerDisplayMode = 'off' | 'countdown' | 'clockTime';

const EMPTY_TABLE_TIMERS: TableTimerDictionary = {};

export interface ReservationServiceContextState {
  floorPlansBySelectedDate: HostFloorPlanData[];
  timersByFloorPlanTableId: TableTimerDictionary;
  selectedFloorPlan: HostFloorPlanData | undefined;
  setSelectedFloorPlanId: Dispatch<SetStateAction<string | undefined>>;
  handleSetGuestSearchQuery: (query: string) => void;
  hasInitialDataLoaded: boolean;
  isLoading: boolean;
  occupants: ServiceOccupant[];
  refreshOccupants: () => void;
  closeSidePanelSheet: () => void;
  selectedOccupant: ServiceOccupant | undefined | null;
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  /** Restricts the occupant lists to one event; null shows everything */
  selectedEventId: string | null;
  setSelectedEventId: (eventId: string | null) => void;
  setSidePanelSheet: (state: SidePanelSheetState) => void;
  shouldShowServerBadges: boolean;
  shouldShowServiceStatuses: boolean;
  shouldShowTableNumbers: boolean;
  /** Lightspeed spend on seated tables; only offered when Lightspeed is enabled. */
  shouldShowSpend: boolean;
  timerDisplayMode: TimerDisplayMode;
  cycleTimerDisplayMode: () => void;
  /** The action sheet that is displaying on top of the default view */
  sidePanelSheet: SidePanelSheetState;
  toggleShouldShowServerBadges: () => void;
  toggleShouldShowServiceStatuses: () => void;
  toggleShouldShowTableNumbers: () => void;
  toggleShouldShowSpend: () => void;
}

export const ReservationServiceContext =
  createContext<ReservationServiceContextState | null>(null);

export const useReservationServiceContext =
  (): ReservationServiceContextState =>
    useDefinedContext(ReservationServiceContext);

export const START_OF_DAY = '00:00:00';

const getStoredFloorPlanId = (restaurantId: string): string | null =>
  sessionStorage.getItem(`service-floor-plan-${restaurantId}`);

const storeFloorPlanId = (restaurantId: string, floorPlanId: string): void => {
  sessionStorage.setItem(`service-floor-plan-${restaurantId}`, floorPlanId);
};

export const ReservationServiceContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { id: restaurantId } = useRestaurant();
  const { searchParams, updateDate } = useServiceSearchParams();

  const [sidePanelSheet, setSidePanelSheet] = useState<SidePanelSheetState>({
    mode: SidePanelSheetMode.None,
  });
  const [shouldShowServerBadges, setShouldShowServerBadges] = useState(true);
  const [shouldShowServiceStatuses, setShouldShowServiceStatuses] =
    useState(true);
  const [shouldShowTableNumbers, setShouldShowTableNumbers] = useState(true);
  const [shouldShowSpend, setShouldShowSpend] = useState(false);
  const [timerDisplayMode, setTimerDisplayMode] =
    useState<TimerDisplayMode>('off');
  const [floorPlansBySelectedDate, setFloorPlansBySelectedDate] = useState<
    HostFloorPlanData[]
  >([]);
  const [selectedFloorPlanId, setSelectedFloorPlanId] = useState<string>();
  const [tableTimersByFloorPlanTableId, setTableTimersByFloorPlanTableId] =
    useState<TableTimerDictionary>(EMPTY_TABLE_TIMERS);
  const clearTableTimersByFloorPlanTableId = () =>
    setTableTimersByFloorPlanTableId(EMPTY_TABLE_TIMERS);
  const [guestSearchQuery, setGuestSearchQuery] = useState<string>();
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  const [isFloorPlanLoading, setIsFloorPlanLoading] = useState(true);
  const [numberOfTimesDataLoaded, setNumberOfTimesDataLoaded] = useState(0);

  const occupantIdFromSearchParams = searchParams.get(OCCUPANT_ID_PARAM);
  const occupantTypeFromSearchParams = searchParams.get(OCCUPANT_TYPE_PARAM);
  const dateFromSearchParams = searchParams.get(DATE_PARAM);

  const initialSelectedDate = dateFromSearchParams
    ? parse(dateFromSearchParams, DATE_PARAM_FORMAT, new Date())
    : new Date();

  const [selectedDate, setSelectedDateValue] = useState(initialSelectedDate);
  const setSelectedDate = (value: Date) => {
    setSelectedDateValue(value);
    updateDate(value);
  };

  const {
    data: occupants = [],
    rerun: loadOccupants,
    isPending: isOccupantsLoading,
  } = useAbortEffect(
    {
      effect: (signal) =>
        getOccupants({
          restaurantId,
          startDate: format(selectedDate, DATE_PARAM_FORMAT),
          startTime: START_OF_DAY,
          endDate: format(addDays(selectedDate, 1), DATE_PARAM_FORMAT),
          endTime: END_OF_RESTAURANT_DAY,
          guestSearchQuery,
          signal,
        }),
    },
    [restaurantId, selectedDate, guestSearchQuery],
  );

  const setError = useError();

  const fetchTableTimers = async () => {
    try {
      const tableTimersResponse = await getTableTimers(restaurantId);

      const result = tableTimersResponse.reduce((dictionary, tableTimer) => {
        dictionary[tableTimer.floorPlanTableId] = tableTimer;
        return dictionary;
      }, {} as TableTimerDictionary);
      setTableTimersByFloorPlanTableId(result);
    } catch (error) {
      setError(error);
    }
  };

  // what happens if the fetched occupants (up to 50 to start) lack the id from params,
  // or there is no search params? Do we handle those cases appropriately/non-breakingly
  const selectedOccupantFromOccupants = occupants.find(
    (occupant) =>
      occupant.id === occupantIdFromSearchParams &&
      occupant.type === occupantTypeFromSearchParams,
  );

  // TODO: Can this be removed? Does it depend on the removal of the paginated
  // occupants fetch?
  const selectedOccupantIdToFetch = useMemo(() => {
    const shouldFetch =
      occupantIdFromSearchParams &&
      occupantTypeFromSearchParams &&
      !selectedOccupantFromOccupants &&
      !isOccupantsLoading;

    return shouldFetch
      ? {
          id: occupantIdFromSearchParams,
          type: occupantTypeFromSearchParams as OccupantType,
        }
      : null;
  }, [sidePanelSheet, isOccupantsLoading, selectedOccupantFromOccupants]);

  // TODO: Same thing - can this be removed?
  const {
    occupant: selectedOccupantFetched,
    invalidate: refreshSelectedOccupant,
  } = useFetchOccupantDetails(selectedOccupantIdToFetch);

  const selectedOccupant =
    selectedOccupantFromOccupants || selectedOccupantFetched;

  const selectedFloorPlan = floorPlansBySelectedDate.find(
    (fp) => fp.id === selectedFloorPlanId,
  );

  const closeSidePanelSheet = () => {
    setSidePanelSheet({ mode: SidePanelSheetMode.None });
  };

  const MINIMUM_SEARCH_CHARS = 3;
  const DEBOUNCE_SEARCH_MS = 1000;

  const debouncedRefreshOccupants = useMemo(
    () =>
      debounce(() => {
        refreshOccupants();
      }, DEBOUNCE_SEARCH_MS),
    [],
  );

  const handleSetGuestSearchQuery = (query: string) => {
    if (query.length < MINIMUM_SEARCH_CHARS) {
      setGuestSearchQuery(undefined);
    } else {
      setGuestSearchQuery(query);
    }

    void debouncedRefreshOccupants();
  };

  const toggleShouldShowServerBadges = () => {
    setShouldShowServerBadges(!shouldShowServerBadges);
  };

  const toggleShouldShowServiceStatuses = () => {
    setShouldShowServiceStatuses(!shouldShowServiceStatuses);
  };

  const toggleShouldShowTableNumbers = () => {
    setShouldShowTableNumbers(!shouldShowTableNumbers);
  };

  const toggleShouldShowSpend = () => {
    setShouldShowSpend(!shouldShowSpend);
  };

  const cycleTimerDisplayMode = () => {
    if (timerDisplayMode === 'off') {
      setTimerDisplayMode('countdown');
      void fetchTableTimers();
    } else if (timerDisplayMode === 'countdown') {
      setTimerDisplayMode('clockTime');
    } else {
      setTimerDisplayMode('off');
      clearTableTimersByFloorPlanTableId();
    }
  };

  const refreshOccupants = () => {
    loadOccupants();
    refreshSelectedOccupant();
  };

  const fetchHostFloorPlans = () => {
    void (async () => {
      setIsFloorPlanLoading(true);
      try {
        const floorPlanResponse = await getHostFloorPlansForRestaurantId(
          restaurantId,
          toISODateFormat(selectedDate),
        );
        setFloorPlansBySelectedDate(floorPlanResponse);

        const storedId = getStoredFloorPlanId(restaurantId);
        const isStoredIdValid = floorPlanResponse.some(
          (fp) => fp.id === storedId,
        );
        setSelectedFloorPlanId(
          isStoredIdValid ? storedId! : floorPlanResponse[0]?.id,
        );

        if (timerDisplayMode !== 'off') await fetchTableTimers();
      } catch (error) {
        setError(error);
      } finally {
        setIsFloorPlanLoading(false);
      }
    })();
  };

  useEffect(() => {
    if (
      !isOccupantsLoading &&
      !isFloorPlanLoading &&
      numberOfTimesDataLoaded === 0
    ) {
      setNumberOfTimesDataLoaded(1);
    }
  }, [isOccupantsLoading, isFloorPlanLoading]);

  useEffect(() => {
    fetchHostFloorPlans();
  }, [restaurantId, selectedDate]);

  useEffect(() => {
    setSelectedEventId(null);
  }, [restaurantId, selectedDate]);

  useServerSentEvents(restaurantId, () => {
    loadOccupants();
    fetchHostFloorPlans();
  });

  useEffect(() => {
    if (selectedFloorPlanId) {
      storeFloorPlanId(restaurantId, selectedFloorPlanId);
    }
  }, [selectedFloorPlanId, restaurantId]);

  const value = useMemo<ReservationServiceContextState>(
    () => ({
      timersByFloorPlanTableId: tableTimersByFloorPlanTableId,
      floorPlansBySelectedDate,
      closeSidePanelSheet,
      handleSetGuestSearchQuery,
      hasInitialDataLoaded: numberOfTimesDataLoaded === 1,
      isLoading: isOccupantsLoading || isFloorPlanLoading,
      occupants,
      refreshOccupants,
      refreshSelectedOccupant,
      selectedDate,
      setSelectedDate,
      selectedEventId,
      setSelectedEventId,
      selectedOccupant,
      selectedFloorPlan,
      setSelectedFloorPlanId,
      setSidePanelSheet,
      shouldShowServerBadges,
      shouldShowServiceStatuses,
      shouldShowTableNumbers,
      shouldShowSpend,
      timerDisplayMode,
      cycleTimerDisplayMode,
      sidePanelSheet,
      toggleShouldShowServerBadges,
      toggleShouldShowServiceStatuses,
      toggleShouldShowTableNumbers,
      toggleShouldShowSpend,
    }),
    [
      tableTimersByFloorPlanTableId,
      selectedFloorPlan,
      guestSearchQuery,
      numberOfTimesDataLoaded,
      occupants,
      selectedDate,
      selectedEventId,
      selectedOccupant,
      shouldShowServerBadges,
      shouldShowServiceStatuses,
      shouldShowTableNumbers,
      shouldShowSpend,
      timerDisplayMode,
      sidePanelSheet,
    ],
  );

  return (
    <ReservationServiceContext.Provider value={value}>
      {children}
    </ReservationServiceContext.Provider>
  );
};
